import React, { forwardRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import truemedLogo from '../../../assets/images/svg/truemed-logo.svg';
import messages from './messages';
import styles from './TruemedButton.module.scss';
import { CartInStore } from '../../../slices/cart/types';
import truemedService from '../../../services/truemed/truemed';
import { TRUEMED_ORIGIN_URL } from './constants';
import { PAYMENT_BLOCK, TRUEMED_PAYMENT_ERROR } from '../../../blocks/Checkout/SteppedCheckout/constants';
import { TruemedButtonProps } from './types';

const TruemedButton = forwardRef<HTMLButtonElement, TruemedButtonProps>((
  {
    onSuccess,
  },
  ref?
) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const {
    isDraft,
    id,
    billingAddress,
  } = useSelector((state: { cart: CartInStore }) => state.cart);

  const [isLoading, setIsLoading] = useState(false);

  const openTruemedIframe = (iframeUrl: string, idempotencyKey: string) => {
    const iframe = document.createElement('iframe');
    iframe.id = 'truemed-checkout';
    iframe.src = iframeUrl || '';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.position = 'fixed';
    iframe.style.top = '0';
    iframe.style.bottom = '0';
    iframe.style.border = 'none';

    document.body.style.overflow = 'hidden';
    document.body.appendChild(iframe);

    window.addEventListener('message', (event) => {
      const eventData = event.data as unknown as {
        success: boolean;
        error?: string;
        redirectUrl: string;
      };

      if (event.origin === TRUEMED_ORIGIN_URL) {
        if (!eventData || typeof(eventData) !== 'object') {
          dispatch({
            type: 'addError',
            errorBlock: PAYMENT_BLOCK,
            errorCode: TRUEMED_PAYMENT_ERROR,
          });
          dispatch({ type: 'setPaymentStatus', status: 'failed' });
          return;
        }

        if (eventData.success) {
          document.body.style.overflow = '';
          document.body.removeChild(iframe);
          onSuccess({
            address: billingAddress
              ? Object.fromEntries(
                  Object.entries(billingAddress).filter(
                    ([value]) => typeof value === 'string' || typeof value === 'number'
                  )
                )
              : {},
            paymentId: eventData.redirectUrl,
            token: idempotencyKey
          });
        } else {
          // eslint-disable-next-line no-console
          console.error('Truemed payment error:', eventData);
          document.body.removeChild(iframe);
          document.body.style.overflow = '';
          dispatch({
            type: 'addError',
            errorBlock: PAYMENT_BLOCK,
            errorCode: TRUEMED_PAYMENT_ERROR,
          });
          dispatch({ type: 'setPaymentStatus', status: 'failed' });
        }
      }
    });
  };

  const handleTrueMedButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const sessionData = await truemedService.authorizeSession({
        cartId: isDraft ? id : undefined,
        isDraft: isDraft ?? undefined,
      });
      if (!!sessionData && sessionData.redirect_url) {
        setIsLoading(false);
        openTruemedIframe(sessionData.redirect_url, sessionData.idempotencyKey);
      } else {
        throw new Error('Failed to create Truemed session');
      }
      // No explicit return needed here
    } catch (error) {
      dispatch({
        type: 'addError',
        errorBlock: PAYMENT_BLOCK,
        errorCode: TRUEMED_PAYMENT_ERROR,
      });
      dispatch({ type: 'setPaymentStatus', status: 'failed' });
    }
  };

  return (
    <div
      className={styles['truemed-button-wrapper']}
    >
      <div className={styles['truemed-button']} />
      <button
        type="button"
        className={classnames(styles['custom-truemed-button'], 'btn', {
          [styles.disabled]: isLoading,
        })}
        onClick={(e) => {
          handleTrueMedButtonClick(e);
        }}
        id={formatMessage(messages.truemedButtonId)}
        ref={ref}
      >
        <div className={styles['img-container']}>
          <img
            src={truemedLogo as unknown as string}
            alt={formatMessage(messages.truemedAltText)}
          />
        </div>
      </button>
    </div>
  );
});

export default TruemedButton;
